import React from 'react';
import { ICareCheckin, ICareEvent } from '../model/ICheckin';
import { Card } from '../../components/Card';
import { CardHeading } from '../components/CardHeading';
import { CardSubHeading } from '../components/CardSubHeading';
import { IonLabel } from '@ionic/react';
import { IntegerField } from '../../components/IntegerField';
import { CheckinIcon } from '../components/CheckinIcon';
import { CheckinDetailsConfig } from '../model/CheckinDetailsConfig';
import { createUseStyles } from 'react-jss';
import { CareEventTimeSlider } from './CareEventTimeSlider';

const useStyles = createUseStyles({
    tracker: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        margin: '20px 0',

        '&:empty': {
            margin: '10px 0'
        }
    },

    row: {
        gap: 16,
        display: 'flex',
        alignItems: 'flex-start',

        '& > div': {
            minWidth: 80
        }
    }
});

interface ICareTrackerCardProps {
    checkin?: ICareCheckin;
    patientName: string;
    onChangeCareEventHours: (update: { careEvent: ICareEvent, hours: number }) => void;
    onChangeTotalCareHours: (totalCareHours?: number) => void;
    onChangeMilesDriven: (milesDriven?: number) => void;
};

export const CareTrackerCard: React.FC<ICareTrackerCardProps> = ({ checkin, patientName, onChangeCareEventHours, onChangeTotalCareHours, onChangeMilesDriven }) => {
    const classes = useStyles();

    const selectedCareEvents = checkin?.careEvents;
    const config = CheckinDetailsConfig['CARE_EVENTS'];

    const directions = !selectedCareEvents?.length
        ? 'Select care events above to track time for each.'
        : 'Enter total care hours then update the time spent on each care event, OR adjust each slider and Innerhive will add them up for you.';

    // network save
    const onChangeRange = (update: { careEvent: ICareEvent, hours: number }) => {
        onChangeCareEventHours(update);
    };

    return (
        <Card padded>

            <CardHeading>Care Time Tracker</CardHeading>
            <CardSubHeading>{directions}</CardSubHeading>

            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginTop: 10}}>
                <IonLabel>
                    Total time caring for {patientName}
                </IonLabel>
                <IntegerField
                    data-id="total-care-hours"
                    label="hrs"
                    labelPlacement="end"
                    max={24}
                    width={75}
                    value={checkin?.totalCareHours}
                    onChange={onChangeTotalCareHours}
                />
            </div>

            <div data-type="care-tracker" className={classes.tracker}>
                {selectedCareEvents?.map(careEvent => {
                    const event = config.icons.find(obj => obj.label === careEvent);

                    if (!event) {
                        return null;
                    }

                    const value = checkin?.careEventHours?.[careEvent] || 0;

                    return (
                        <div key={event.label} className={classes.row}>
                            <CheckinIcon
                                active
                                key={event.label}
                                Icon={event.Icon}
                                label={event.label}
                                activeColor={event.activeColor}
                            />
                            <CareEventTimeSlider
                                data-id={careEvent}
                                hours={value}
                                activeColor={event?.activeColor}
                                onChange={(hours: number) => onChangeRange({ careEvent, hours })}
                            />
                        </div>
                    );
                })}
            </div>

            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                <IonLabel>
                    Total distance driven
                </IonLabel>
                <IntegerField
                    data-id="miles-driven"
                    label="miles"
                    labelPlacement="end"
                    max={999}
                    width={97}
                    value={checkin?.milesDriven}
                    onChange={onChangeMilesDriven}
                />
            </div>

        </Card>
    );
};
